/* eslint-disable no-magic-numbers */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useParentHistoryMutation from '../hooks/useParentHistoryMutation';
import { parseInitialZoom } from '../hooks/useLinkableSearchState';
import { AppStateProvider } from '../contexts/AppStateContext';
import { MapCommandsProvider } from '../contexts/MapCommandsContext';
import { ViewportProvider } from '../contexts/ViewportContext';
import type { Viewport } from '../contexts/ViewportContext';
import { createMapCommandQueue } from '../state/mapCommands';
import type { ColdAppState } from '../state/appState';
import EmbeddedFooter from './EmbeddedFooter';
import Footer from './Footer';
import MapboxGlSupportBoundry from './MapboxGlSupportBoundry';
import OnboardingBoundry from './OnboardingBoundry';
import StaticBackdrop from './StaticBackdrop';
import isEmbedded from '../helpers/isEmbedded';
import { SVGSprite } from './SVGSprite';

const HUD = lazy(async () => import('./HUD'));
const Map = lazy(async () => import('./Map'));
const MapOverlay = lazy(async () => import('./MapOverlay'));
const MapRail = lazy(async () => import('./MapRail'));

const initialCenter: Viewport['center'] = [-98.0, 38.0];

const initialColdState: ColdAppState = {
  cardProperties: {},
  isCardActive: false,
  // Default to hybrid satellite imagery — more useful to anglers and consistent
  // with the Fishbrain app (RBFF map updates P0).
  mapStyleKey: 'hybrid',
};

const AppContent = () => {
  useParentHistoryMutation();
  const location = useLocation();
  // Live viewport, published by the map on moveend; the armed-centering flag
  // for the next detail card lives beside it.
  const [viewport, setViewport] = useState<Viewport>(() => ({
    bounds: null,
    center: initialCenter,
    zoom: parseInitialZoom(location.search),
  }));
  const [shouldCenter, setShouldCenter] = useState(true);

  // Movement commands fired before the map exists (e.g. onboarding's panTo)
  // are queued and flushed once <Map /> registers its controller.
  const [{ commands: mapCommands, register: registerMapCommands }] = useState(() =>
    createMapCommandQueue(setShouldCenter),
  );

  // Until the map first reports a viewport, the zoom seed follows the URL (the
  // embedded flow adopts the parent hash ?zoom= after mount); afterwards URL
  // changes no longer touch zoom.
  const mapOwnsViewportRef = useRef(false);
  const onViewportChange = useCallback((nextViewport: Viewport) => {
    mapOwnsViewportRef.current = true;
    setViewport(nextViewport);
  }, []);
  useEffect(() => {
    if (!mapOwnsViewportRef.current) {
      setViewport((prevViewport) => ({
        ...prevViewport,
        zoom: parseInitialZoom(location.search),
      }));
    }
  }, [location.search]);

  return (
    <MapCommandsProvider value={mapCommands}>
      <ViewportProvider value={viewport}>
        <SVGSprite />
        <div className="main">
          <OnboardingBoundry>
            <MapboxGlSupportBoundry>
              <Suspense
                fallback={
                  <StaticBackdrop>
                    <div className="loader loader-prompt" />
                  </StaticBackdrop>
                }
              >
                <MapRail />
                <HUD shouldCenter={shouldCenter} />
                <div className="map-area">
                  <Map
                    registerMapCommands={registerMapCommands}
                    onViewportChange={onViewportChange}
                    initialViewport={viewport}
                  />
                  <MapOverlay />
                </div>
              </Suspense>
            </MapboxGlSupportBoundry>
          </OnboardingBoundry>
        </div>
        {isEmbedded() ? <EmbeddedFooter /> : <Footer />}
      </ViewportProvider>
    </MapCommandsProvider>
  );
};

const App = () => (
  <AppStateProvider initialState={initialColdState}>
    <AppContent />
  </AppStateProvider>
);

export default App;
